import { CSSProperties, useEffect, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';

import { cn } from '@utils';

import { Icon, Render } from '@components/Shared';

type Props = {
  title?: string;
  hasError?: boolean;
  description?: string;
  children?: React.ReactNode;
  onToggle?: (state: boolean) => void;
};

export const SchemaCollapsible: React.FC<Props> = ({
  title,
  hasError,
  children,
  description,
  onToggle,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [maxHeight, setMaxHeight] = useState<CSSProperties['maxHeight']>(0);

  const containerRef = useRef<HTMLDivElement>(null);

  // Store the previous children to avoid flickering during transitions
  const prevChildrenRef = useRef<React.ReactNode>(children);

  const handleOpenChange = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    onToggle?.(newState);
  };

  useEffect(() => {
    if (hasError && !isOpen) {
      handleOpenChange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasError]);

  if (isOpen) {
    prevChildrenRef.current = children;
  }

  return (
    <div
      className={cn('w-full rounded-xl border border-neural-02', {
        'border-error': hasError,
      })}
    >
      <button
        type="button"
        onClick={handleOpenChange}
        aria-label="Toggle collapsible field"
        className="relative w-full p-6 text-start transition-[padding-bottom] duration-300 ease-in-out"
      >
        <h6 className="pr-10 text-h6">{title}</h6>
        <Render if={description}>
          <p
            slot="description"
            className="mt-[6px] text-footnote text-neural-04"
          >
            {description}
          </p>
        </Render>

        <div
          className={cn(
            'absolute right-6 top-6 flex items-center justify-center transition-transform duration-300 ease-in-out',
            { 'rotate-180': isOpen },
          )}
        >
          <Icon icon={faChevronDown} className="text-[20px]" />
        </div>
      </button>

      <CSSTransition
        in={isOpen}
        timeout={300}
        onEnter={() => setMaxHeight(0)}
        onEntered={() => setMaxHeight('none')}
        onEntering={() => setMaxHeight(containerRef.current?.scrollHeight ?? 0)}
        onExiting={() => setMaxHeight(0)}
      >
        <div
          ref={containerRef}
          style={{
            maxHeight:
              !isOpen && maxHeight === 'none'
                ? containerRef.current?.scrollHeight
                : maxHeight,
          }}
          className="mx-6 -translate-y-6 overflow-hidden transition-[max-height] duration-300 ease-in-out"
        >
          {prevChildrenRef.current}
        </div>
      </CSSTransition>
    </div>
  );
};
